import { ChangeEvent, FormEvent, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import Button from '../../components/Button';
import Input from '../../components/Input';

import Api from '../api';
import { enlightenLogin } from '../data/pages';
import type { Site } from '../types';
import { validateEmail } from '../utils';

export interface EnlightenData {
  sites: Site[];
  token: string | null;
}

interface FormElements extends HTMLFormControlsCollection {
  enlightenId: HTMLInputElement;
  enlightenPassword: HTMLInputElement;
}

interface EnlightenLoginFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

const INPUT_TEXT_COLOR = '#3c3c3c';
const INPUT_WIDTH = '400px';

export default function EnlightenLogin() {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    enlightenData,
    setEnlightenData,
    setStartedFunnel,
  } = useContext(AppContext);

  const [enlightenId, setEnlightenId] = useState('');
  const [enlightenPassword, setEnlightenPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [idHaveError, setIdHaveError] = useState(false);
  const [loginHaveError, setLoginHaveError] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    enlightenId: enlightenIdData,
    enlightenPassword: enlightenPasswordData,
    forgotPassword,
    heading,
    loginButton,
    nextPage,
  } = enlightenLogin;
  const isButtonDisabled = !(enlightenId && enlightenPassword);

  useEffect(() => {
    setStartedFunnel(true);
  }, []);

  const goToNextPage = () => {
    // set current page index
    setCurrentPageIndex(currentPageIndex + 1);

    // update page param and navigate to next page
    searchParams.set('page', nextPage);
    setSearchParams(searchParams);
  };

  const handleIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoginHaveError(false);
    const enlightenId = event.currentTarget.value;
    if (validateEmail(enlightenId)) {
      setEnlightenId(enlightenId);
      setIdHaveError(false);
    } else {
      setEnlightenId('');
      setIdHaveError(true);
    }
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoginHaveError(false);
    const enlightenPassword = event.currentTarget.value;
    setEnlightenPassword(enlightenPassword);
  };

  const handleSubmit = (event: FormEvent<EnlightenLoginFormElement>) => {
    event.preventDefault();

    const { enlightenId, enlightenPassword } = event.currentTarget.elements;

    setLoginHaveError(false);
    setIsLoggingIn(true);
    Api.loginToEnlighten(enlightenId.value, enlightenPassword.value).then(
      (data) => {
        setIsLoggingIn(false);
        const { site_list, status, token } = data;

        if (status) {
          setEnlightenData({
            ...enlightenData,
            sites: site_list,
            token,
          });
          goToNextPage();
        } else {
          setLoginHaveError(true);
        }
      },
      (error) => {
        setIsLoggingIn(false);
        setLoginHaveError(true);
        console.error('Enlighten login error:', error);
      }
    );
  };

  return (
    <>
      <p className="text-[20px] md:text-[32px] md:mt-10 mb-10 text-black w-full">
        {heading}
      </p>
      <form onSubmit={handleSubmit}>
        <Input
          className={`mt-3 md:mt-4 mb-3 md:mb-5 text-[${INPUT_TEXT_COLOR}] md:w-[${INPUT_WIDTH}]`}
          errorMessage={enlightenIdData.errorMessage}
          haveError={idHaveError}
          name={enlightenIdData.label}
          onChange={handleIdChange}
          placeholder={enlightenIdData.placeholder}
          type={enlightenIdData.type}
        />
        <Input
          className={`mt-2 md:mt-4 mb-3 md:mb-5 text-[${INPUT_TEXT_COLOR}] md:w-[${INPUT_WIDTH}]`}
          errorMessage={enlightenPasswordData.errorMessage}
          haveError={loginHaveError}
          name={enlightenPasswordData.label}
          onChange={handlePasswordChange}
          placeholder={enlightenPasswordData.placeholder}
          type={enlightenPasswordData.type}
        />
        <div
          className={`w-full max-w-[294px] md:max-w-[400px] md-max:max-w-[279px] short:!w-[350px] md:text-center`}>
          <a
            href={forgotPassword.url}
            className={`text-[${INPUT_TEXT_COLOR}]`}
            target="_blank"
            rel="noreferrer">
            {forgotPassword.text}
          </a>
        </div>
        <div className="mt-[50px] md:mt-[50px]">
          <Button
            disabled={isButtonDisabled}
            isLoading={isLoggingIn}
            title={loginButton.title}
            type={loginButton.type}
            data-zuko-id="enlighten-login_continue"
          />
        </div>
      </form>
    </>
  );
}
